import { useMemo } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { filterByBusinessUnitScope } from '@/lib/business-units/businessUnitScope';
import { apiGet, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { JobType } from '@/types/settings';

type JobTypeApiSkill = Omit<JobType['requiredSkills'][number], 'minLevel'> & {
  minLevel?: number | null;
  min_level?: number | null;
};

type JobTypeApiRow = Partial<JobType> & {
  id: string;
  name: string;
  job_count?: number;
  short_code?: string;
  default_duration?: number;
  default_priority?: JobType['defaultPriority'];
  recurrence_frequency?: JobType['recurrenceFrequency'];
  recurrence_interval?: number;
  min_crew_size?: number;
  default_price?: number | string | null;
  line_items?: JobType['lineItems'];
  pricing_model?: JobType['pricingModel'];
  billing_category?: string | null;
  customer_phrases?: string[];
  is_service_call_fallback?: boolean;
  service_call_enabled?: boolean;
  service_call_fee_policy?: string | null;
  visit_deposit_enabled?: boolean;
  visit_deposit_type?: 'FLAT' | 'PERCENT';
  visit_deposit_value?: number | string | null;
  iris_survey_questions?: JobType['irisSurveyQuestions'];
  iris_ask_equipment?: boolean;
  required_skills?: JobTypeApiSkill[];
  is_active?: boolean;
  jobCount?: number;
  is_default?: boolean;
  pipeline_id?: string | null;
  business_unit_ids?: string[];
  business_units?: JobType['businessUnits'];
  authorization_config?: JobType['authorizationConfig'];
  signoff_config?: JobType['signoffConfig'];
  arrival_window_config?: JobType['arrivalWindowConfig'];
  scheduling_defaults?: JobType['schedulingDefaults'];
  cancellation_window_hours?: number | null;
  cancellation_fee_type?: JobType['cancellationFeeType'];
  cancellation_fee_amount?: number | string | null;
  deposit_refundable?: boolean;
  default_stage_id?: string | null;
  confirmation_method?: JobType['confirmationMethod'];
  invoicing_method?: JobType['invoicingMethod'];
  invoicing_schedule?: JobType['invoicingSchedule'];
  auto_invoice?: boolean | null;
  auto_charge?: boolean | null;
  review_request_default?: boolean | null;
  weather_policy?: JobType['weatherPolicy'];
  created_at?: string;
  updated_at?: string;
};

function normalizeOptionalNumber(value: number | string | null | undefined) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function preferCamelCase<T>(camelCase: T | undefined, snakeCase: T | undefined): T | undefined {
  return camelCase !== undefined ? camelCase : snakeCase;
}

function normalizeRequiredSkills(
  skills: JobTypeApiSkill[] | undefined,
): JobType['requiredSkills'] | undefined {
  if (skills === undefined) return undefined;

  return skills.map((skill) => ({
    ...skill,
    minLevel: skill.minLevel !== undefined ? skill.minLevel : (skill.min_level ?? null),
  }));
}

export function normalizeJobType(row: JobTypeApiRow): JobType {
  const isServiceCallFallback =
    preferCamelCase(row.isServiceCallFallback, row.is_service_call_fallback) ?? false;
  return {
    ...row,
    jobCount: row.jobCount ?? row.job_count ?? 0,
    shortCode: preferCamelCase(row.shortCode, row.short_code),
    defaultDuration: preferCamelCase(row.defaultDuration, row.default_duration),
    defaultPriority: preferCamelCase(row.defaultPriority, row.default_priority),
    recurrenceFrequency: preferCamelCase(row.recurrenceFrequency, row.recurrence_frequency),
    recurrenceInterval: preferCamelCase(row.recurrenceInterval, row.recurrence_interval),
    minCrewSize: preferCamelCase(row.minCrewSize, row.min_crew_size),
    defaultPrice:
      row.defaultPrice !== undefined
        ? row.defaultPrice
        : normalizeOptionalNumber(row.default_price),
    lineItems: preferCamelCase(row.lineItems, row.line_items),
    pricingModel: preferCamelCase(row.pricingModel, row.pricing_model),
    billingCategory: preferCamelCase(row.billingCategory, row.billing_category),
    customerPhrases: preferCamelCase(row.customerPhrases, row.customer_phrases) ?? [],
    isServiceCallFallback,
    scopeMode:
      row.scopeMode === 'DIAGNOSTIC' ||
      preferCamelCase(row.serviceCallEnabled, row.service_call_enabled) === true ||
      row.irisMode === 'SERVICE_CALL' ||
      isServiceCallFallback
        ? 'DIAGNOSTIC'
        : 'DEFINED',
    // Rolling-deploy compatibility mirror.
    serviceCallEnabled:
      row.scopeMode === 'DIAGNOSTIC' ||
      preferCamelCase(row.serviceCallEnabled, row.service_call_enabled) === true ||
      row.irisMode === 'SERVICE_CALL' ||
      isServiceCallFallback,
    serviceCallFeePolicy:
      preferCamelCase(row.serviceCallFeePolicy, row.service_call_fee_policy) ?? null,
    // Visit deposit — upfront deposit before the visit (payment link at booking).
    visitDepositEnabled:
      preferCamelCase(row.visitDepositEnabled, row.visit_deposit_enabled) ?? false,
    visitDepositType: preferCamelCase(row.visitDepositType, row.visit_deposit_type) ?? 'FLAT',
    visitDepositValue:
      row.visitDepositValue !== undefined
        ? (normalizeOptionalNumber(row.visitDepositValue) ?? 0)
        : (normalizeOptionalNumber(row.visit_deposit_value) ?? 0),
    // Iris intake survey + equipment awareness (Decisions D+E).
    irisSurveyQuestions: preferCamelCase(row.irisSurveyQuestions, row.iris_survey_questions) ?? [],
    irisAskEquipment: preferCamelCase(row.irisAskEquipment, row.iris_ask_equipment) ?? false,
    requiredSkills: normalizeRequiredSkills(
      preferCamelCase(row.requiredSkills, row.required_skills),
    ),
    isActive: row.isActive ?? row.is_active ?? true,
    isDefault: row.isDefault ?? row.is_default ?? false,
    pipelineId: preferCamelCase(row.pipelineId, row.pipeline_id) ?? null,
    businessUnitIds: preferCamelCase(row.businessUnitIds, row.business_unit_ids) ?? [],
    businessUnits: preferCamelCase(row.businessUnits, row.business_units),
    authorizationConfig: preferCamelCase(row.authorizationConfig, row.authorization_config),
    signoffConfig: preferCamelCase(row.signoffConfig, row.signoff_config),
    arrivalWindowConfig: preferCamelCase(row.arrivalWindowConfig, row.arrival_window_config),
    schedulingDefaults: preferCamelCase(row.schedulingDefaults, row.scheduling_defaults),
    cancellationWindowHours: preferCamelCase(
      row.cancellationWindowHours,
      row.cancellation_window_hours,
    ),
    cancellationFeeType: preferCamelCase(row.cancellationFeeType, row.cancellation_fee_type),
    cancellationFeeAmount:
      row.cancellationFeeAmount !== undefined
        ? row.cancellationFeeAmount
        : normalizeOptionalNumber(row.cancellation_fee_amount),
    depositRefundable: preferCamelCase(row.depositRefundable, row.deposit_refundable),
    defaultStageId: preferCamelCase(row.defaultStageId, row.default_stage_id),
    confirmationMethod: preferCamelCase(row.confirmationMethod, row.confirmation_method),
    invoicingMethod: preferCamelCase(row.invoicingMethod, row.invoicing_method),
    invoicingSchedule: preferCamelCase(row.invoicingSchedule, row.invoicing_schedule),
    autoInvoice: preferCamelCase(row.autoInvoice, row.auto_invoice),
    autoCharge: preferCamelCase(row.autoCharge, row.auto_charge),
    reviewRequestDefault: preferCamelCase(row.reviewRequestDefault, row.review_request_default),
    weatherPolicy: preferCamelCase(row.weatherPolicy, row.weather_policy),
    createdAt: preferCamelCase(row.createdAt, row.created_at),
    updatedAt: preferCamelCase(row.updatedAt, row.updated_at),
  } as JobType;
}

export function useJobTypes(options: { enabled?: boolean } = {}) {
  return useApiQuery<JobType[]>({
    queryKey: settingsKeys.jobTypes(),
    queryFn: async () => {
      const rows = await apiGet<JobTypeApiRow[]>(operationsClient, settingsEndpoints.jobTypes());
      return rows.map(normalizeJobType);
    },
    enabled: options.enabled,
    showErrorToast: false,
  });
}

export function useActiveJobTypes(options: { enabled?: boolean } = {}) {
  const query = useJobTypes(options);
  const activeJobTypes = useMemo(
    () => (query.data ?? []).filter((jobType) => jobType.isActive),
    [query.data],
  );

  return { ...query, data: activeJobTypes };
}

/**
 * Active job types narrowed to one Business Unit.
 *
 * A sibling of `useActiveJobTypes` rather than an extra argument on it: that
 * hook has ~20 callers and none of them want a new parameter. The whole list
 * is fetched either way (one cached query shared with every other caller) and
 * narrowed here, because job types already carry `businessUnitIds`.
 *
 * `businessUnitId` undefined/null ⇒ NO filtering; an item with an EMPTY
 * `businessUnitIds` matches EVERY business unit. See
 * lib/business-units/businessUnitScope.
 */
export function useActiveJobTypesForBusinessUnit(
  businessUnitId?: string | null,
  options: { enabled?: boolean } = {},
) {
  const query = useActiveJobTypes(options);
  const { data } = query;
  const scopedJobTypes = useMemo(
    () => filterByBusinessUnitScope(data, businessUnitId),
    [data, businessUnitId],
  );

  return { ...query, data: scopedJobTypes };
}
